// Product information tabs (blocks/product-tabs.liquid), the WAI-ARIA tabs pattern with automatic
// activation: a tab is selected as soon as it takes focus. The server renders the first tab
// selected and the other panels hidden, so this script only moves the selection.
//   Left / Right: previous / next tab, wrapping round
//   Home / End: first / last tab
//   Click: that tab
// Roving tabindex: only the selected tab is in the tab order; Tab from it goes into its panel.
// Gate: scripts/gates/check-a11y-harness.mjs (checkProductTabs).
class ProductTabs extends HTMLElement {
  #controller = new AbortController();

  /** @returns {HTMLElement[]} */
  get tabs() {
    return Array.from(this.querySelectorAll(':scope > [role="tablist"] > [role="tab"]'));
  }

  connectedCallback() {
    const { signal } = this.#controller;
    const list = this.querySelector(':scope > [role="tablist"]');
    if (!list) return;
    list.addEventListener('click', this.#handleClick, { signal });
    list.addEventListener('keydown', this.#handleKeyDown, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  /**
   * Selects a tab, shows its panel and hides the others.
   * @param {HTMLElement} tab
   * @param {boolean} [focus]
   */
  select(tab, focus = false) {
    for (const t of this.tabs) {
      const selected = t === tab;
      t.setAttribute('aria-selected', String(selected));
      t.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls') || '');
      if (panel) panel.hidden = !selected;
    }
    if (focus) tab.focus();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (target instanceof HTMLElement && this.tabs.includes(target)) this.select(target);
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    const tabs = this.tabs;
    const current = tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    if (current < 0) return;
    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const tab = tabs[next];
    if (tab) this.select(tab, true);
  };
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
